import Link from "next/link";
import { get, truncate, isEmpty, forEach, filter, join } from "lodash";
import useWishlist from "grandus-lib/hooks/useWishlist";
import useCompare from "grandus-lib/hooks/useCompare";
import EnhancedEcommerce from "grandus-lib/utils/ecommerce";
import TagManager from "grandus-lib/utils/gtag";
import { useRouter } from "next/router";

import ProductLabelsPhoto from "./ProductLabelsPhoto";

import dynamic from "next/dynamic";
import useMember from "utils/useMember";
import useSWR from "swr";
const Image = dynamic(() =>
  import("grandus-lib/components-atomic/image/Image")
);
const Price = dynamic(() =>
  import("grandus-lib/components-atomic/price/Price")
);

import styles from "components/product/card/ProductCard.module.scss";
import ProductLabelsBase from "./ProductLabelsBase";

const CompareButton = ({ productId }) => {
  const { isLoading, itemAdd, itemRemove, itemExists } = useCompare();

  const text = isLoading
    ? "fa-spinner fa-spin"
    : !itemExists(productId)
    ? "fa-balance-scale"
    : "fa-balance-scale-right";

  return (
    <span
      className={`${styles?.action} action`}
      style={itemExists(productId) ? { opacity: 1 } : {}}
      onClick={() =>
        !isLoading
          ? itemExists(productId)
            ? itemRemove(productId)
            : itemAdd(productId)
          : null
      }
    >
      <i className={`fas ${text}`}></i>
    </span>
  );
};

const WishlistButton = ({ productId }) => {
  const { isLoading, itemAdd, itemRemove, itemExists } = useWishlist();

  const text = isLoading
    ? "fas fa-spinner fa-spin"
    : !itemExists(productId)
    ? "far fa-heart"
    : "fas fa-heart";

  return (
    <span
      className={`${styles?.action} action`}
      style={itemExists(productId) ? { opacity: 1 } : {}}
      onClick={() =>
        !isLoading
          ? itemExists(productId)
            ? itemRemove(productId)
            : itemAdd(productId)
          : null
      }
    >
      <i className={text}></i>
    </span>
  );
};

const EshopAvailability = ({ product }) => {
  const { member, operationUnits } = useMember();
  const districtId = get(
    operationUnits,
    [member?.district, 0, "town", "district", "id"],
    null
  );
  const { data: availableStores, isValidating: isLoading } = useSWR(
    districtId && product
      ? `/api/pages/product/${product.id}/availability?districtId=${districtId}`
      : null,
    {
      revalidateOnFocus: false,
    }
  );

  if (isLoading) {
    return <i className="fas fa-spinner fa-spin"></i>;
  }

  if (!isEmpty(availableStores)) {
    const available =
      product?.storeStatus?.available && availableStores.length > 0;
    return (
      <>
        <span className={`${available ? "green" : "red"} availibility`}></span>
        <p>{available ? "na sklade" : "na objednávku"}</p>
      </>
    );
  }

  return null;
};

const StoresAvailability = ({ store }) => {
  const { member } = useMember();
  let availableStores = get(store, "[0].storeData", []);
  if (!isEmpty(member?.operationUnit?.name)) {
    availableStores = filter(availableStores, [
      "storeName",
      member?.operationUnit?.name,
    ]);
  }
  if (isEmpty(member?.operationUnit?.name) && !isEmpty(member?.district)) {
    availableStores = filter(availableStores, [
      "storeDistrict",
      member?.district,
    ]);
  }

  return (
    <>
      <span
        className={`${
          availableStores.length > 0 ? "green" : "red"
        } availibility`}
      ></span>
      {availableStores.length > 0 ? (
        <p>
          {!isEmpty(member?.operationUnit?.name) ? (
            <>dostupné v predajni</>
          ) : (
            <>
              dnes v{" "}
              <strong>
                {availableStores.length === 1
                  ? `${availableStores.length} predajni`
                  : `${availableStores.length} predajniach`}
              </strong>
            </>
          )}
        </p>
      ) : (
        <p>
          nedostupné v{" "}
          {!isEmpty(member?.operationUnit?.name) ? "predajni" : "predajniach"}
        </p>
      )}
    </>
  );
};

const ProductCard = ({ options = {}, ...product }) => {
  const {
    id,
    name,
    brand,
    urlTitle,
    finalPriceData,
    priceData,
    photo,
    shortDescription,
    storeStatus,
    marketingSets,
  } = product;
  const classNames = get(options, "classNames", []);
  const router = useRouter();
  const title = {
    prefixes: [],
    suffixes: [],
  };
  forEach(marketingSets, (set) => {
    const { prefix, suffix } = set;
    if (prefix) {
      title.prefixes.push(prefix);
    }
    if (suffix) {
      title.suffixes.push(suffix);
    }
  });

  const onClickHandler = (e) => {
    e.preventDefault();
    TagManager.push(
      EnhancedEcommerce.productClick(
        { ...product, ...{ position: get(options, "eecProduct.position") } },
        "category",
        {
          page: get(options, "eecProduct.page"),
          perPage: get(options, "eecProduct.perPage"),
        }
      ),
      () => {
        router.push("/produkt/[id]", `/produkt/${urlTitle}`);
      }
    );
  };
  return (
    <div
      className={`${join(classNames, ", ")} ${styles?.productCard} item ${
        styles?.minHeight
      }`}
    >
      <div className={`${styles?.top} top`}>
        <Link href="/produkt/[id]" as={`/produkt/${urlTitle}`}>
          <a className={`${styles?.productImage} product-image`}>
            <Image
              photo={photo}
              size={"180x185"}
              type={"jpg"}
              useNextImage={true}
            />
          </a>
        </Link>
        <ProductLabelsBase product={product} />
        <ProductLabelsPhoto product={product} productCard={true} />
        <span className={`${styles?.manufacturer} manufacturer`}>
          {brand.name}
        </span>
        <h2>
          <Link href="/produkt/[id]" as={`/produkt/${urlTitle}`}>
            <a className={`${styles?.breakWord}`}>
              {!isEmpty(title.prefixes) ? title.prefixes.join(" ") : ""}
              {name}
              {!isEmpty(title.suffixes)
                ? ` + ${title.suffixes.join(" + ")}`
                : ""}
            </a>
          </Link>
        </h2>
        <p className={`${styles?.desc} desc`}>
          <article
            dangerouslySetInnerHTML={{
              __html: truncate(shortDescription ? shortDescription : "", {
                length: 120,
              }),
            }}
          />
        </p>
      </div>
      <div className={`${styles?.bottom} bottom`}>
        <div className={`${styles?.priceWrap} price-wrap`}>
          <Price
            priceData={finalPriceData}
            options={{
              hideVatPrice: true,
              mainPriceClass: `${styles?.price} price`,
            }}
          />
          {priceData?.price !== finalPriceData?.price ? (
            <Price
              priceData={priceData}
              options={{
                hideVatPrice: true,
                mainPriceClass: `${styles?.oldPrice} old-price`,
              }}
            />
          ) : (
            ""
          )}
        </div>
        <div className={`${styles?.actions} actions`}>
          <Link href="/produkt/[id]" as={`/produkt/${urlTitle}`}>
            <a className={`${styles?.button} button`} onClick={onClickHandler}>
              Detail
            </a>
          </Link>
          <div className="my-2 hidden-sm-down">
            <WishlistButton productId={id} />
            <CompareButton productId={id} />
          </div>
        </div>
        {/* <div className={`${styles?.store} store`}>
          <div
            className={`${styles?.storeItem} store-item`}
            style={{ width: "40%", textAlign: "center" }}
          >
            <EshopAvailability product={product} />
          </div>
          <div
            className={`${styles?.storeItem} store-item`}
            style={{ width: "60%", textAlign: "center" }}
          >
            <StoresAvailability store={store} />
          </div>
        </div> */}
        <div className={`${styles?.store}`}>
          <div className={`${styles?.storeItem}`}>
            <span
              className={`${
                storeStatus?.available ? styles?.green : styles?.red
              } ${styles?.availibility}`}
            ></span>
            {storeStatus?.message}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
